import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useStore } from "zustand";
import Cell from "./Cell";
import TopMenuBar from "./TopMenuBar";
import SelectionPalette from "./SelectionPalette";
import { useEdgeDragging } from "../hooks/useEdgeDragging";
// pdfExport is loaded dynamically in handleExport to avoid bundling jsPDF upfront
import { exportLayoutToPNG } from "../utils/pngExport";
import { deleteUpload } from "../api/uploads";
import { prepareImageForPanel, imageFromDataTransfer, dragHasFiles } from "../utils/imageImport";
import {
  normalizePortraitDimensions,
  pageSizeToPx,
  findPageSizePreset,
} from "../constants/pageDimensions";
import {
  defaultCellLayoutTemplate,
  findCellLayoutTemplate,
  matchCellLayoutTemplateByGrid,
} from "../constants/cellLayoutTemplates";
import { trackEvent } from "../utils/analytics";
import { migrateTailPlacement } from "../utils/tailGeometry";
import { sameGeometry, remapIntoCells, adjustCellsInPlace, sortReadingOrder } from "../utils/relayout";
import { pageStorageKey, registerPendingSaveFlusher } from "../utils/pageStorage";
import { markPlaced } from "../utils/motion";
import { buildSamplePage } from "../constants/samplePage";
import { mergeCellGroup, unmergeCell } from "../utils/panelGeometry";
import { SHORTCUTS, matchesShortcut } from "../constants/shortcuts";
import { BLEED_PX, SAFE_ZONE_PX, GUIDE_COLORS } from "../constants/printGuides";
import { findComicStyle, halftoneBackground } from "../constants/comicStyles";
import { DRAFTING_TABLE_PROPS } from "../constants/featureFlags";
import AIGenerateModal from "./AIGenerateModal";
import CanvasViewport from "./CanvasViewport";
import LayoutTools from "./LayoutTools";
import InkSprites from "./InkSprites";
import ShotNotes from "./ShotNotes";
import { createBoardCells, findBoardLayout, frameHasContent, normalizeStoryboard } from "../constants/storyboard";
import { createEditorStore } from "../store/useEditorStore";
import { EditorStoreContext } from "../store/EditorStoreContext";

const SAVE_DEBOUNCE_MS = 500;
const TIPS_DISMISSED_KEY = "striply_tips_dismissed";

// Grid for a template key, including ad-hoc "custom_RxC" grids
const gridForTemplateKey = (key) => {
  const template = findCellLayoutTemplate(key);
  if (template) return template.grid;
  const match = /^custom_(\d+)x(\d+)$/.exec(key || "");
  return match ? { rows: Number(match[1]), columns: Number(match[2]) } : null;
};

const migrateBubbleTails = (cells) =>
  cells.map((cell) => ({
    ...cell,
    items: (cell.items || []).map((item) => {
      if (item.type !== "speechBubble" || item.tailTipX != null) return item;
      const coords = migrateTailPlacement(
        item.tailPlacement || "bottom",
        item.width || 160,
        item.height || 96
      );
      return { ...item, ...coords, tailWidth: item.tailWidth || 20 };
    }),
  }));

const FluidLayoutEditor = ({ layout, onPanelCountChange }) => {
  const containerRef = useRef(null);
  const saveTimeoutRef = useRef(null);
  const [showTips, setShowTips] = useState(() => {
    try {
      return !localStorage.getItem(TIPS_DISMISSED_KEY);
    } catch (e) {
      return true;
    }
  });

  // Create a fresh Zustand store for this component instance.
  // Because ProjectEditor uses key={pageId}, each page gets its own store.
  const storeRef = useRef(null);
  if (!storeRef.current) {
    storeRef.current = createEditorStore();
  }
  const store = storeRef.current;

  // Helper to read current store state imperatively (for event handlers)
  const s = () => store.getState();

  // Subscribe to individual store values for rendering
  const cells = useStore(store, (st) => st.cells);
  const selection = useStore(store, (st) => st.selection);
  const gutterSize = useStore(store, (st) => st.gutterSize);
  const pageSizeKeyState = useStore(store, (st) => st.pageSizeKey);
  const showGuides = useStore(store, (st) => st.showGuides);
  const darkMode = useStore(store, (st) => st.darkMode);
  const showAIModal = useStore(store, (st) => st.showAIModal);
  const contextMenu = useStore(store, (st) => st.contextMenu);
  const clipboardBubble = useStore(store, (st) => st.clipboardBubble);
  const containerSize = useStore(store, (st) => st.containerSize);
  const comicStyleKey = useStore(store, (st) => st.comicStyleKey);
  const isSample = useStore(store, (st) => st.isSample);
  const storyboard = useStore(store, (st) => st.storyboard);
  const comicStyle = findComicStyle(comicStyleKey);

  const storageKey = pageStorageKey(layout?.id);
  const onPanelCountChangeRef = useRef(onPanelCountChange);
  onPanelCountChangeRef.current = onPanelCountChange;

  // ─── Derived page dimensions ────────────────────────────────────────────
  const pagePreset = findPageSizePreset(pageSizeKeyState);
  const pagePx = pageSizeToPx(pagePreset);
  const pageWidth = layout?.page_width || pagePx.width;
  const pageHeight = layout?.page_height || pagePx.height;
  const { width: portraitWidth, height: portraitHeight } = pageSizeKeyState === "letter"
    ? normalizePortraitDimensions(pageWidth, pageHeight)
    : { width: pagePx.width, height: pagePx.height };
  // Shots are numbered through the whole project: earlier pages' frames first
  const shotOffset = layout?.shotOffset || 0;

  // ─── Edge dragging ──────────────────────────────────────────────────────
  const { startDragEdge } = useEdgeDragging(
    cells,
    (newCells) => s().setCells(newCells),
    containerSize,
    layout,
    () => s().beginGesture(),
    gutterSize,
    () => s().endGesture(),
    () => s().zoom
  );

  // ─── Cell creation ──────────────────────────────────────────────────────
  const createInitialCells = useCallback(
    (gridOverride = null, gutter = s().gutterSize, dimensions = null) => {
      const containerWidth = dimensions?.width || portraitWidth;
      const containerHeight = dimensions?.height || portraitHeight;

      const layoutCells = Array.isArray(layout?.cells) ? layout.cells : null;
      const useLayoutCells = !gridOverride && layoutCells && layoutCells.length > 0;

      const rows = useLayoutCells
        ? Math.max(...layoutCells.map((cell) => (cell.row_index ?? 0) + (cell.row_span ?? 1)))
        : gridOverride?.rows ?? 3;
      const cols = useLayoutCells
        ? Math.max(...layoutCells.map((cell) => (cell.col_index ?? 0) + (cell.col_span ?? 1)))
        : gridOverride?.columns ?? 3;

      const availableWidth = containerWidth - 2 * gutter;
      const availableHeight = containerHeight - 2 * gutter;
      const totalHorizGutter = Math.max(0, (cols - 1) * gutter);
      const totalVertGutter = Math.max(0, (rows - 1) * gutter);
      const colWidth = cols > 0 ? (availableWidth - totalHorizGutter) / cols : availableWidth;
      const rowHeight = rows > 0 ? (availableHeight - totalVertGutter) / rows : availableHeight;

      const fallbackCells = [];
      if (!useLayoutCells) {
        for (let row = 0; row < rows; row++) {
          for (let col = 0; col < cols; col++) {
            fallbackCells.push({
              row_index: row,
              col_index: col,
              row_span: 1,
              col_span: 1,
              cell_key: `R${row + 1}C${col + 1}`,
            });
          }
        }
      }

      const cellsDefinition = useLayoutCells ? layoutCells : fallbackCells;

      const sortedCells = [...cellsDefinition].sort((a, b) => {
        const rowDiff = (a.row_index ?? 0) - (b.row_index ?? 0);
        if (rowDiff !== 0) return rowDiff;
        return (a.col_index ?? 0) - (b.col_index ?? 0);
      });

      return sortedCells.map((cellDef) => {
        const rowIndex = cellDef.row_index ?? 0;
        const colIndex = cellDef.col_index ?? 0;
        const rowSpan = cellDef.row_span ?? 1;
        const colSpan = cellDef.col_span ?? 1;
        const cellId = `cell-${rowIndex}-${colIndex}`;
        const width = colWidth * colSpan + gutter * (colSpan - 1);
        const height = rowHeight * rowSpan + gutter * (rowSpan - 1);
        const items = [];

        return {
          id: cellId,
          key: cellDef.cell_key ?? `R${rowIndex + 1}C${colIndex + 1}`,
          x: gutter + colIndex * (colWidth + gutter),
          y: gutter + rowIndex * (rowHeight + gutter),
          width,
          height,
          items,
        };
      });
    },
    [layout, portraitHeight, portraitWidth, store]
  );

  // ─── Initialization ─────────────────────────────────────────────────────
  useEffect(() => {
    if (s().isInitialized) return;

    s().setContainerSize({ width: portraitWidth, height: portraitHeight });
    if (containerRef.current) {
      const el = containerRef.current;
      s().setContainerSize({ width: el.offsetWidth, height: el.offsetHeight });
    }

    // Try localStorage
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed.cells) && parsed.cells.length > 0) {
          const restoredCells = parsed.cells.map((cell) => ({
            ...cell,
            // Strip legacy in-panel tip cards; tips now live on the work surface
            items: (cell.items || []).filter((i) => i.type !== "tipCard"),
          }));

          const partialState = {};
          if (parsed.gutterSize != null) partialState.gutterSize = parsed.gutterSize;
          if (parsed.pageSizeKey) partialState.pageSizeKey = parsed.pageSizeKey;
          if (parsed.comicStyleKey) partialState.comicStyleKey = parsed.comicStyleKey;
          if (parsed.isSample) partialState.isSample = true;
          if (parsed.storyboard) partialState.storyboard = normalizeStoryboard(parsed.storyboard);

          // Restore template, or infer it for layouts saved before it was persisted
          if (parsed.layoutTemplateKey) {
            partialState.layoutTemplateKey = parsed.layoutTemplateKey;
          } else if (restoredCells.length > 0) {
            const rows = Math.ceil(Math.sqrt(restoredCells.length));
            const cols = Math.ceil(restoredCells.length / rows);
            const matched = matchCellLayoutTemplateByGrid(rows, cols);
            if (matched) partialState.layoutTemplateKey = matched.key;
          }

          s().setInitialized(migrateBubbleTails(restoredCells), partialState);
          return;
        }
      }
    } catch (e) {
      console.warn("Failed to load saved layout:", e);
    }

    // A new storyboard page: frames from the board settings it was created with
    if (layout?.board) {
      const seed = layout.board;
      const pageSizeKey = seed.pageSizeKey || "letter_landscape";
      const dims = pageSizeToPx(findPageSizePreset(pageSizeKey));
      const boardSettings = normalizeStoryboard(seed.storyboard || {});
      s().setInitialized(
        createBoardCells({ pageWidth: dims.width, pageHeight: dims.height, gutter: s().gutterSize, storyboard: boardSettings }),
        {
          storyboard: boardSettings,
          pageSizeKey,
          ...(seed.comicStyleKey ? { comicStyleKey: seed.comicStyleKey } : {}),
        }
      );
      return;
    }

    // A brand-new project's first page is the sample page
    if (layout?.starter) {
      s().setInitialized(buildSamplePage(portraitWidth, portraitHeight, s().gutterSize), {
        layoutTemplateKey: defaultCellLayoutTemplate.key,
        isSample: true,
      });
      return;
    }

    // No saved state — create fresh cells
    let initialTemplate = defaultCellLayoutTemplate;
    if (Array.isArray(layout?.cells) && layout.cells.length > 0) {
      const inferredRows = Math.max(
        ...layout.cells.map((cell) => (cell.row_index ?? 0) + (cell.row_span ?? 1))
      );
      const inferredCols = Math.max(
        ...layout.cells.map((cell) => (cell.col_index ?? 0) + (cell.col_span ?? 1))
      );
      const matchedTemplate = matchCellLayoutTemplateByGrid(inferredRows, inferredCols);
      if (matchedTemplate) initialTemplate = matchedTemplate;
    }

    s().setInitialized(createInitialCells(initialTemplate.grid), {
      layoutTemplateKey: initialTemplate.key,
    });
  }, [createInitialCells, layout?.cells, portraitHeight, portraitWidth, storageKey, store]);

  // ─── Auto-save ──────────────────────────────────────────────────────────
  useEffect(() => {
    const pageId = layout?.id;
    let pendingState = null;

    const writeSave = () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
      const state = pendingState;
      pendingState = null;
      if (!state) return;
      try {
        const serializableCells = state.cells.map((cell) => ({
          ...cell,
          items: (cell.items || []).filter(
            (item) => item.type !== "ad" && item.type !== "tipCard"
          ),
        }));
        localStorage.setItem(storageKey, JSON.stringify({
          cells: serializableCells,
          savedAt: Date.now(),
          version: 1,
          gutterSize: state.gutterSize,
          pageSizeKey: state.pageSizeKey,
          comicStyleKey: state.comicStyleKey,
          layoutTemplateKey: state.layoutTemplateKey,
          ...(state.storyboard ? { storyboard: state.storyboard } : {}),
          ...(state.isSample ? { isSample: true } : {}),
        }));
      } catch (e) {
        console.warn("Failed to save layout:", e);
        const quotaHit = e && (e.name === "QuotaExceededError" || e.code === 22);
        store.getState().showNotice(
          quotaHit
            ? "Couldn't save this page: the browser's storage is full. Use Export JSON to keep a copy, or remove some large art."
            : "Couldn't save this page. Use Export JSON to keep a copy of your work.",
          { sticky: true }
        );
      }
      if (onPanelCountChangeRef.current) onPanelCountChangeRef.current(pageId, state.cells.length);
    };

    const unsubscribe = store.subscribe((state, prevState) => {
      if (!state.isInitialized) return;
      if (state.cells === prevState.cells && state.gutterSize === prevState.gutterSize && state.pageSizeKey === prevState.pageSizeKey && state.comicStyleKey === prevState.comicStyleKey && state.layoutTemplateKey === prevState.layoutTemplateKey && state.isSample === prevState.isSample && state.storyboard === prevState.storyboard) return;
      if (state.cells.length === 0) return;

      pendingState = state;
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(writeSave, SAVE_DEBOUNCE_MS);
    });

    // A brand-new page is saved right away, so it has a thumbnail and can be
    // duplicated or exported before its first edit
    const initialState = store.getState();
    let hasSavedData = true;
    try {
      hasSavedData = Boolean(localStorage.getItem(storageKey));
    } catch (e) {
      // storage unavailable: nothing to do
    }
    if (initialState.isInitialized && initialState.cells.length > 0 && !hasSavedData) {
      pendingState = initialState;
      writeSave();
    } else if (initialState.isInitialized && onPanelCountChangeRef.current) {
      onPanelCountChangeRef.current(pageId, initialState.cells.length);
    }

    // Never drop the last edit: flush on page switch, duplicate, or tab close
    const unregisterFlusher = registerPendingSaveFlusher(writeSave);
    window.addEventListener("beforeunload", writeSave);

    return () => {
      unsubscribe();
      unregisterFlusher();
      window.removeEventListener("beforeunload", writeSave);
      writeSave();
    };
  // storageKey is derived from layout.id
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, storageKey]);

  // ─── Window resize ──────────────────────────────────────────────────────
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const el = containerRef.current;
        s().setContainerSize({ width: el.offsetWidth, height: el.offsetHeight });
      } else {
        s().setContainerSize({ width: portraitWidth, height: portraitHeight });
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [portraitHeight, portraitWidth, store]);

  // ─── Handlers ───────────────────────────────────────────────────────────

  const handleUploadBackground = async (cellId, file) => {
    try {
      const base64Url = await prepareImageForPanel(file);
      const imageId = `img-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      s().applyCellUpdate((prevCells) =>
        prevCells.map((cell) => {
          if (cell.id !== cellId) return cell;
          const otherItems = (cell.items || []).filter((item) => item.type !== "background");
          return {
            ...cell,
            items: [{
              id: `background-${imageId}`,
              type: "background",
              url: base64Url,
              filename: file.name,
              createdAt: Date.now(),
            }, ...otherItems],
          };
        })
      );
      trackEvent("image_uploaded", { cell_id: cellId });
    } catch (error) {
      console.error("Failed to upload background image", error);
      s().showNotice(error?.message === "That file isn't an image"
        ? "That file isn't an image — panels take PNG, JPEG, GIF, or WebP art."
        : "Couldn't read that image. Please try another file.");
    }
  };

  const handleClearTopItem = async (cellId) => {
    let removedItem = null;
    s().applyCellUpdate((prevCells) => {
      let changed = false;
      const nextCells = prevCells.map((cell) => {
        if (cell.id !== cellId) return cell;
        if (!cell.items || cell.items.length === 0) return cell;
        changed = true;
        removedItem = cell.items[cell.items.length - 1];
        return { ...cell, items: cell.items.slice(0, -1) };
      });
      return changed ? nextCells : prevCells;
    });
    if (removedItem?.type === "background" && removedItem.uploadId) {
      try { await deleteUpload(removedItem.uploadId); } catch (error) {
        console.error("Failed to delete cleared background", error);
      }
    }
  };

  // `at` (panel-local px) places a smaller bubble centred there, ready to
  // type into: double-click-to-letter.
  const handleAddSpeechBubble = (cellId, at = null) => {
    let newId = null;
    s().applyCellUpdate((prevCells) => {
      let changed = false;
      const nextCells = prevCells.map((cell) => {
        if (cell.id !== cellId) return cell;
        const maxWidth = Math.max(80, cell.width - 24);
        const defaultWidth = at
          ? Math.max(90, Math.min(170, maxWidth))
          : Math.max(120, Math.min(260, cell.width * 0.55, maxWidth));
        const defaultHeight = at
          ? Math.max(60, Math.min(90, cell.height - 24))
          : Math.max(80, Math.min(160, cell.height - 24));
        const bubbleWidth = Math.max(90, Math.min(defaultWidth, cell.width - 24));
        const bubbleHeight = Math.max(60, Math.min(defaultHeight, cell.height - 24));
        // Stagger new items so successive bubbles/SFX don't stack on each other
        const stagger = ((cell.items || []).filter((i) => i.type !== "background").length % 5) * 22;
        const x = at
          ? Math.max(0, Math.min(cell.width - bubbleWidth, at.x - bubbleWidth / 2))
          : Math.max(12, Math.min(cell.width - defaultWidth - 12, 48 + stagger));
        const y = at
          ? Math.max(0, Math.min(cell.height - bubbleHeight, at.y - bubbleHeight / 2))
          : Math.max(12, Math.min(cell.height - defaultHeight - 12, 48 + stagger));
        newId = markPlaced(`speech-${Date.now()}-${Math.round(Math.random() * 1000)}`, { edit: Boolean(at) });
        changed = true;
        return {
          ...cell,
          items: [...(cell.items || []), {
            id: newId,
            type: "speechBubble",
            text: "",
            shape: "rounded",
            tailEnabled: true,
            x,
            y,
            width: bubbleWidth,
            height: bubbleHeight,
            tailTipX: bubbleWidth / 2,
            tailTipY: bubbleHeight + 30,
            tailWidth: 20,
          }],
        };
      });
      return changed ? nextCells : prevCells;
    });
    if (at && newId) s().setSelection({ type: "bubble", cellId, bubbleId: newId });
    trackEvent("bubble_added", { cell_id: cellId, via: at ? "double_click" : "toolbar" });
  };

  const handleUpdateSpeechBubble = (cellId, bubbleId, updates) => {
    s().applyCellUpdate((prevCells) => {
      let changed = false;
      const nextCells = prevCells.map((cell) => {
        if (cell.id !== cellId) return cell;
        const nextItems = (cell.items || []).map((item) => {
          if (item.type !== "speechBubble" || item.id !== bubbleId) return item;
          changed = true;
          return { ...item, ...updates };
        });
        return { ...cell, items: nextItems };
      });
      return changed ? nextCells : prevCells;
    });
  };

  const handleRemoveSpeechBubble = (cellId, bubbleId) => {
    s().applyCellUpdate((prevCells) => {
      let changed = false;
      const nextCells = prevCells.map((cell) => {
        if (cell.id !== cellId) return cell;
        const filtered = (cell.items || []).filter(
          (item) => item.type !== "speechBubble" || item.id !== bubbleId
        );
        if (filtered.length !== (cell.items || []).length) changed = true;
        return { ...cell, items: filtered };
      });
      return changed ? nextCells : prevCells;
    });
    const current = s().selection;
    if (current?.type === "bubble" && current.bubbleId === bubbleId) {
      s().setSelection({ type: "cell", cellId });
    }
  };

  const handleDismissCell = useCallback(async (cellId) => {
    let backgroundToDelete = null;
    let removed = false;
    s().applyCellUpdate((prevCells) => {
      const cellToRemove = prevCells.find((cell) => cell.id === cellId);
      if (!cellToRemove) return prevCells;
      removed = true;
      backgroundToDelete = (cellToRemove.items || []).find((item) => item.type === "background");
      return prevCells.filter((cell) => cell.id !== cellId);
    });
    if (!removed) return;
    trackEvent("panel_deleted", { cell_id: cellId });
    if (backgroundToDelete?.uploadId) {
      try { await deleteUpload(backgroundToDelete.uploadId); } catch (error) {
        console.error("Failed to delete background for dismissed cell", error);
      }
    }
    const current = s().selection;
    if (current?.cellId === cellId) s().clearSelection();
  }, [store]);

  const handleUpdateCell = (cellId, updates) => {
    s().applyCellUpdate((prevCells) => {
      let changed = false;
      const nextCells = prevCells.map((cell) => {
        if (cell.id !== cellId) return cell;
        changed = true;
        return { ...cell, ...updates };
      });
      return changed ? nextCells : prevCells;
    });
  };

  const handleUpdateItem = (cellId, itemId, updates) => {
    s().applyCellUpdate((prevCells) => {
      let changed = false;
      const nextCells = prevCells.map((cell) => {
        if (cell.id !== cellId) return cell;
        const nextItems = (cell.items || []).map((item) => {
          if (item.id !== itemId) return item;
          changed = true;
          return { ...item, ...updates };
        });
        return { ...cell, items: nextItems };
      });
      return changed ? nextCells : prevCells;
    });
  };

  const handleAddSFX = (cellId) => {
    s().applyCellUpdate((prevCells) => {
      let changed = false;
      const nextCells = prevCells.map((cell) => {
        if (cell.id !== cellId) return cell;
        const stagger = ((cell.items || []).filter((i) => i.type !== "background").length % 5) * 22;
        changed = true;
        return {
          ...cell,
          items: [...(cell.items || []), {
            id: markPlaced(`sfx-${Date.now()}-${Math.round(Math.random() * 1000)}`),
            type: "sfx",
            text: "BOOM",
            fontSize: 64,
            rotation: -12,
            x: Math.max(10, Math.min(cell.width * 0.15 + stagger, cell.width - 80)),
            y: Math.max(10, Math.min(cell.height * 0.3 + stagger, cell.height - 60)),
          }],
        };
      });
      return changed ? nextCells : prevCells;
    });
    trackEvent("sfx_added", { cell_id: cellId });
  };

  const handleUpdateSFX = (cellId, sfxId, updates) => {
    s().applyCellUpdate((prevCells) => {
      let changed = false;
      const nextCells = prevCells.map((cell) => {
        if (cell.id !== cellId) return cell;
        const nextItems = (cell.items || []).map((item) => {
          if (item.type !== "sfx" || item.id !== sfxId) return item;
          changed = true;
          return { ...item, ...updates };
        });
        return { ...cell, items: nextItems };
      });
      return changed ? nextCells : prevCells;
    });
  };

  const handleRemoveSFX = (cellId, sfxId) => {
    s().applyCellUpdate((prevCells) => {
      let changed = false;
      const nextCells = prevCells.map((cell) => {
        if (cell.id !== cellId) return cell;
        const filtered = (cell.items || []).filter((item) => item.type !== "sfx" || item.id !== sfxId);
        if (filtered.length !== (cell.items || []).length) changed = true;
        return { ...cell, items: filtered };
      });
      return changed ? nextCells : prevCells;
    });
    const current = s().selection;
    if (current?.type === "sfx" && current.sfxId === sfxId) {
      s().setSelection({ type: "cell", cellId });
    }
  };

  const handleDuplicateBubble = (cellId, bubbleId) => {
    const cell = s().cells.find((c) => c.id === cellId);
    if (!cell) return;
    const bubble = (cell.items || []).find((i) => i.type === "speechBubble" && i.id === bubbleId);
    if (!bubble) return;
    const newId = markPlaced(`speech-${Date.now()}-${Math.round(Math.random() * 1000)}`);
    s().applyCellUpdate((prevCells) => {
      let changed = false;
      const nextCells = prevCells.map((c) => {
        if (c.id !== cellId) return c;
        changed = true;
        return {
          ...c,
          items: [...(c.items || []), {
            ...bubble,
            id: newId,
            x: (bubble.x || 16) + 15,
            y: (bubble.y || 16) + 15,
          }],
        };
      });
      return changed ? nextCells : prevCells;
    });
    s().setSelection({ type: "bubble", cellId, bubbleId: newId });
  };

  const handleDuplicateSFX = (cellId, sfxId) => {
    const cell = s().cells.find((c) => c.id === cellId);
    if (!cell) return;
    const sfx = (cell.items || []).find((i) => i.type === "sfx" && i.id === sfxId);
    if (!sfx) return;
    const newId = markPlaced(`sfx-${Date.now()}-${Math.round(Math.random() * 1000)}`);
    s().applyCellUpdate((prevCells) => {
      let changed = false;
      const nextCells = prevCells.map((c) => {
        if (c.id !== cellId) return c;
        changed = true;
        return {
          ...c,
          items: [...(c.items || []), {
            ...sfx,
            id: newId,
            x: (sfx.x || 20) + 15,
            y: (sfx.y || 20) + 15,
          }],
        };
      });
      return changed ? nextCells : prevCells;
    });
    s().setSelection({ type: "sfx", cellId, sfxId: newId });
  };

  const handleExport = async () => {
    try {
      const opts = {
        pageWidthPx: portraitWidth, pageHeightPx: portraitHeight, dpi: s().exportDpi, comicStyleKey: s().comicStyleKey,
        storyboard: s().storyboard, shotOffset,
      };
      if (s().exportFormat === "png") {
        await exportLayoutToPNG(s().cells, layout?.name || "layout", opts);
      } else {
        const { exportLayoutToPDF } = await import("../utils/pdfExport");
        await exportLayoutToPDF(s().cells, layout?.name || "layout", opts);
      }
      trackEvent("export_image", { format: s().exportFormat, dpi: s().exportDpi });
    } catch (error) {
      alert(`Failed to export ${s().exportFormat.toUpperCase()}. Please try again.`);
    }
  };

  // Swap to a new panel grid, carrying each panel's content across in
  // reading order. Only asks first if some content would have nowhere to go.
  const applyPanelGrid = (grid, templateKey) => {
    const { cells: nextCells, orphanedCount } = remapIntoCells(s().cells, createInitialCells(grid));
    if (orphanedCount > 0 && !window.confirm(
      `This layout has fewer panels. Content in ${orphanedCount} panel${orphanedCount === 1 ? "" : "s"} won't fit and will be removed (you can undo).`
    )) return false;
    s().captureSnapshot();
    s().setLayoutTemplateKey(templateKey);
    s().setCells(nextCells);
    s().clearSelection();
    return true;
  };

  const handleLayoutTemplateChange = (templateKey) => {
    const template = findCellLayoutTemplate(templateKey);
    if (!template) return;
    if (applyPanelGrid(template.grid, template.key)) {
      trackEvent("layout_selected", { template_key: template.key });
    }
  };

  const handleCustomGrid = (rows, cols) => {
    const r = Math.max(1, Math.min(10, rows));
    const c = Math.max(1, Math.min(10, cols));
    const matched = matchCellLayoutTemplateByGrid(r, c);
    if (applyPanelGrid({ rows: r, columns: c }, matched ? matched.key : `custom_${r}x${c}`)) {
      trackEvent("layout_selected", { template_key: `custom_${r}x${c}`, rows: r, columns: c });
    }
  };

  // Re-flow the current panels for a new gutter and/or page size, keeping all
  // content. An untouched template grid is rebuilt exactly; a hand-adjusted
  // layout is reshaped in place so its proportions survive.
  const relayoutPage = (nextGutter, nextDims) => {
    const { cells: currentCells, gutterSize: currentGutter, layoutTemplateKey } = s();
    const from = { width: portraitWidth, height: portraitHeight, gutter: currentGutter };
    const to = { width: nextDims.width, height: nextDims.height, gutter: nextGutter };
    const grid = gridForTemplateKey(layoutTemplateKey);
    if (grid && sameGeometry(currentCells, createInitialCells(grid, currentGutter, from))) {
      return remapIntoCells(currentCells, createInitialCells(grid, nextGutter, to)).cells;
    }
    return adjustCellsInPlace(currentCells, from, to);
  };

  // Board pages are always generated: rebuild the frames and carry each
  // frame's art, lettering and notes across in shot order.
  const rebuildBoard = ({ storyboard: nextBoard = s().storyboard, dims = { width: portraitWidth, height: portraitHeight }, gutter = s().gutterSize } = {}) => {
    const fresh = createBoardCells({ pageWidth: dims.width, pageHeight: dims.height, gutter, storyboard: nextBoard });
    return remapIntoCells(s().cells, fresh);
  };

  const handleStoryboardChange = (updates) => {
    const current = s().storyboard;
    if (!current) return;
    const next = { ...current, ...updates };
    const reshapes = next.aspectKey !== current.aspectKey || next.layoutKey !== current.layoutKey;
    if (reshapes) {
      const { cells: nextCells } = rebuildBoard({ storyboard: next });
      // Count notes too: a frame with only notes still has work in it
      const orphanedCount = sortReadingOrder(s().cells).slice(nextCells.length).filter(frameHasContent).length;
      if (orphanedCount > 0 && !window.confirm(
        `This board has fewer frames. ${orphanedCount} frame${orphanedCount === 1 ? "" : "s"} with content won't fit and will be removed (you can undo).`
      )) return;
      s().captureSnapshot();
      s().setStoryboard(next);
      s().setCells(nextCells);
      s().clearSelection();
    } else {
      s().captureSnapshot();
      s().setStoryboard(next);
    }
    Object.keys(updates).forEach((key) => trackEvent("storyboard_setting_changed", { setting: key, value: String(updates[key]) }));
  };

  const handlePageSizeChange = (key) => {
    const dims = pageSizeToPx(findPageSizePreset(key));
    s().captureSnapshot();
    const nextCells = s().storyboard ? rebuildBoard({ dims }).cells : relayoutPage(s().gutterSize, dims);
    s().setPageSizeKey(key);
    s().setCells(nextCells);
  };

  const handleGutterSizeChange = (newGutter) => {
    s().captureSnapshot();
    const nextCells = s().storyboard
      ? rebuildBoard({ gutter: newGutter }).cells
      : relayoutPage(newGutter, { width: portraitWidth, height: portraitHeight });
    s().setGutterSize(newGutter);
    s().setCells(nextCells);
  };

  const handleClearSavedData = () => {
    if (window.confirm("Clear all saved data and start fresh? This cannot be undone.")) {
      try { localStorage.removeItem(storageKey); } catch (e) {
        console.warn("Failed to clear saved data:", e);
      }
      const template = defaultCellLayoutTemplate;
      if (s().storyboard) {
        s().setCells(createBoardCells({ pageWidth: portraitWidth, pageHeight: portraitHeight, gutter: s().gutterSize, storyboard: s().storyboard }));
      } else {
        s().setLayoutTemplateKey(template.key);
        s().setCells(createInitialCells(template.grid));
      }
      s().clearSelection();
      store.setState({ history: [] });
    }
  };

  const handleExportJSON = () => {
    try {
      const exportCells = s().cells.map((cell) => ({
        ...cell,
        items: (cell.items || []).filter(
          (item) => item.type !== "ad" && item.type !== "tipCard"
        ),
      }));
      const data = {
        version: 1,
        exportedAt: new Date().toISOString(),
        layout: {
          id: layout?.id,
          name: layout?.name || "Untitled Layout",
          page_width: layout?.page_width || portraitWidth,
          page_height: layout?.page_height || portraitHeight,
        },
        cells: exportCells,
      };
      const json = JSON.stringify(data, null, 2);
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${layout?.name || "layout"}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Failed to export JSON:", e);
      alert("Failed to export layout. Please try again.");
    }
  };

  const handleImportJSON = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        if (!data.cells || !Array.isArray(data.cells)) {
          throw new Error("Invalid layout file: missing cells array");
        }
        const importedCells = data.cells.map((cell) => ({
          ...cell,
          items: (cell.items || []).filter((i) => i.type !== "tipCard"),
        }));
        s().captureSnapshot();
        s().setCells(migrateBubbleTails(importedCells));
        s().clearSelection();
        if (importedCells.length > 0) {
          const rows = Math.ceil(Math.sqrt(importedCells.length));
          const cols = Math.ceil(importedCells.length / rows);
          const matched = matchCellLayoutTemplateByGrid(rows, cols);
          if (matched) s().setLayoutTemplateKey(matched.key);
        }
      } catch (err) {
        console.error("Failed to import JSON:", err);
        alert("Failed to import layout: " + err.message);
      }
    };
    reader.readAsText(file);
  };

  const handleMergePanels = useCallback(() => {
    const { multiSelect, cells, gutterSize: gutter } = s();
    if (multiSelect.length < 2) return;
    const merged = mergeCellGroup(cells, multiSelect, gutter);
    if (!merged) {
      s().showNotice("Those panels can't merge — the combined panel would cover another one.");
      return;
    }
    s().applyCellUpdate(() => merged);
    s().setMultiSelect([]);
    s().setSelection({ type: "cell", cellId: multiSelect[0] });
  }, [store]);

  const handleUnmergePanels = useCallback(() => {
    const { selection, cells } = s();
    if (!selection?.cellId) return;
    const restored = unmergeCell(cells, selection.cellId);
    if (!restored) return;
    s().applyCellUpdate(() => restored);
    s().setMultiSelect([]);
    s().clearSelection();
  }, [store]);

  // ─── Derived values ──────────────────────────────────────────────────────
  const boardLayout = storyboard ? findBoardLayout(storyboard.layoutKey) : null;
  const selectedCell = useMemo(() => {
    if (!selection?.cellId) return null;
    return cells.find((cell) => cell.id === selection.cellId) || null;
  }, [cells, selection]);

  const selectedBubble = useMemo(() => {
    if (selection?.type !== "bubble" || !selection.bubbleId || !selectedCell) return null;
    return (selectedCell.items || []).find(
      (item) => item.type === "speechBubble" && item.id === selection.bubbleId
    ) || null;
  }, [selectedCell, selection]);

  const handleCopyBubble = useCallback(() => {
    if (selection?.type === "bubble" && selectedBubble) {
      s().setClipboardBubble({ ...selectedBubble });
    }
  }, [selection, selectedBubble, store]);

  const handlePasteBubble = useCallback((cellId) => {
    const cb = s().clipboardBubble;
    if (!cb) return;
    const targetId = cellId || s().selection?.cellId;
    if (!targetId) return;
    s().applyCellUpdate((prevCells) => {
      let changed = false;
      const nextCells = prevCells.map((cell) => {
        if (cell.id !== targetId) return cell;
        changed = true;
        return {
          ...cell,
          items: [...(cell.items || []), {
            ...cb,
            id: markPlaced(`speech-${Date.now()}-${Math.round(Math.random() * 1000)}`),
            x: (cb.x || 16) + 15,
            y: (cb.y || 16) + 15,
          }],
        };
      });
      return changed ? nextCells : prevCells;
    });
  }, [store]);

  const handleContextMenuAction = useCallback((action) => {
    const cellId = s().contextMenu?.cellId;
    s().setContextMenu(null);
    if (!cellId) return;
    const layerDirections = { layerForward: "forward", layerBackward: "backward", layerToFront: "toFront", layerToBack: "toBack" };
    if (layerDirections[action]) {
      const sel = s().selection;
      const itemId = sel?.type === "bubble" ? sel.bubbleId : sel?.type === "sfx" ? sel.sfxId : null;
      if (itemId && cellId) s().reorderItem(cellId, itemId, layerDirections[action]);
      return;
    }
    switch (action) {
      case "addBubble": handleAddSpeechBubble(cellId); break;
      case "addSFX": handleAddSFX(cellId); break;
      case "copyBubble": handleCopyBubble(); break;
      case "pasteBubble": handlePasteBubble(cellId); break;
      case "delete": handleDismissCell(cellId); break;
      default: break;
    }
  }, [handleCopyBubble, handlePasteBubble, handleDismissCell, store]);


  // ─── Keyboard shortcuts ─────────────────────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e) => {
      const tag = e.target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;

      if (matchesShortcut(e, SHORTCUTS.UNDO)) {
        e.preventDefault(); s().undo();
      } else if (matchesShortcut(e, SHORTCUTS.REDO)) {
        e.preventDefault(); s().redo();
      } else if (matchesShortcut(e, SHORTCUTS.DELETE) || matchesShortcut(e, SHORTCUTS.BACKSPACE_DELETE)) {
        const sel = s().selection;
        if (sel?.type === "bubble") {
          const cell = s().cells.find((c) => c.id === sel.cellId);
          const bubble = cell && (cell.items || []).find((i) => i.type === "speechBubble" && i.id === sel.bubbleId);
          if (bubble) { e.preventDefault(); handleRemoveSpeechBubble(cell.id, bubble.id); }
        }
      } else if (matchesShortcut(e, SHORTCUTS.DESELECT)) {
        e.preventDefault();
        if (s().knifeMode) s().setKnifeMode(false);
        else s().clearSelection();
      } else if (matchesShortcut(e, SHORTCUTS.EXPORT_PDF)) {
        e.preventDefault(); handleExport();
      } else if ((e.metaKey || e.ctrlKey) && e.key === "d") {
        e.preventDefault();
        const sel = s().selection;
        if (sel?.type === "bubble" && sel.cellId && sel.bubbleId) {
          handleDuplicateBubble(sel.cellId, sel.bubbleId);
        } else if (sel?.type === "sfx" && sel.cellId && sel.sfxId) {
          handleDuplicateSFX(sel.cellId, sel.sfxId);
        }
      } else if ((e.metaKey || e.ctrlKey) && e.key === "c" && s().selection?.type === "bubble") {
        e.preventDefault(); handleCopyBubble();
      } else if ((e.metaKey || e.ctrlKey) && e.key === "v" && s().clipboardBubble) {
        // Don't preventDefault: the browser's paste event runs first and, if
        // the system clipboard holds an image, pastes that instead.
        pendingBubblePasteRef.current = window.setTimeout(() => {
          pendingBubblePasteRef.current = null;
          handlePasteBubble();
        }, 0);
      } else if (matchesShortcut(e, SHORTCUTS.LAYER_TO_FRONT) || matchesShortcut(e, SHORTCUTS.LAYER_TO_BACK) ||
                 matchesShortcut(e, SHORTCUTS.LAYER_FORWARD) || matchesShortcut(e, SHORTCUTS.LAYER_BACKWARD)) {
        const sel = s().selection;
        const itemId = sel?.type === "bubble" ? sel.bubbleId : sel?.type === "sfx" ? sel.sfxId : null;
        if (itemId && sel.cellId) {
          e.preventDefault();
          const direction = matchesShortcut(e, SHORTCUTS.LAYER_TO_FRONT) ? "toFront"
            : matchesShortcut(e, SHORTCUTS.LAYER_TO_BACK) ? "toBack"
            : matchesShortcut(e, SHORTCUTS.LAYER_FORWARD) ? "forward" : "backward";
          s().reorderItem(sel.cellId, itemId, direction);
        }
      } else if (e.key.startsWith("Arrow") && s().selection?.type === "bubble") {
        const sel = s().selection;
        const cell = s().cells.find((c) => c.id === sel.cellId);
        const bubble = cell && (cell.items || []).find((i) => i.type === "speechBubble" && i.id === sel.bubbleId);
        if (bubble) {
          e.preventDefault();
          const step = e.shiftKey ? (s().snapGridSize || 10) : 1;
          const dx = e.key === "ArrowLeft" ? -step : e.key === "ArrowRight" ? step : 0;
          const dy = e.key === "ArrowUp" ? -step : e.key === "ArrowDown" ? step : 0;
          handleUpdateSpeechBubble(cell.id, bubble.id, {
            x: Math.max(0, bubble.x + dx),
            y: Math.max(0, bubble.y + dy),
          });
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleCopyBubble, handlePasteBubble, store]);

  // ─── Paste and drop art ─────────────────────────────────────────────────
  const pendingBubblePasteRef = useRef(null);

  useEffect(() => {
    const handlePaste = (event) => {
      const target = event.target;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) return;
      const file = imageFromDataTransfer(event.clipboardData);
      if (!file) return;
      event.preventDefault();
      if (pendingBubblePasteRef.current) {
        window.clearTimeout(pendingBubblePasteRef.current);
        pendingBubblePasteRef.current = null;
      }
      const cellId = s().selection?.cellId;
      if (!cellId) {
        s().showNotice("Select a panel first, then paste to put the art in it.");
        return;
      }
      handleUploadBackground(cellId, file);
    };

    // Files dropped on the desk (not a panel) would otherwise make the
    // browser navigate away to the image.
    const guardDragOver = (event) => {
      if (dragHasFiles(event.dataTransfer)) event.preventDefault();
    };
    // Panels stop propagation when they take a drop, so this only sees misses
    const guardDrop = (event) => {
      if (!dragHasFiles(event.dataTransfer)) return;
      event.preventDefault();
      s().showNotice("Drop art onto a panel to fill it.");
    };

    window.addEventListener("paste", handlePaste);
    window.addEventListener("dragover", guardDragOver);
    window.addEventListener("drop", guardDrop);
    return () => {
      window.removeEventListener("paste", handlePaste);
      window.removeEventListener("dragover", guardDragOver);
      window.removeEventListener("drop", guardDrop);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store]);

  // ─── Stable callbacks for TopMenuBar (avoids re-creating on every render) ──
  const menuHandlers = useMemo(() => ({
    onUpdateItem: (itemId, updates) => {
      const sc = s().cells.find((c) => c.id === s().selection?.cellId);
      if (sc) handleUpdateItem(sc.id, itemId, updates);
    },
    onUpdateSFX: (updates) => {
      const sel = s().selection;
      const sc = s().cells.find((c) => c.id === sel?.cellId);
      if (sc && sel?.type === "sfx" && sel.sfxId) {
        handleUpdateSFX(sc.id, sel.sfxId, updates);
      }
    },
    onRemoveSFX: () => {
      const sel = s().selection;
      const sc = s().cells.find((c) => c.id === sel?.cellId);
      if (sc && sel?.type === "sfx" && sel.sfxId) {
        handleRemoveSFX(sc.id, sel.sfxId);
      }
    },
    onUpdateSpeechBubble: (updates) => {
      const sel = s().selection;
      const sc = s().cells.find((c) => c.id === sel?.cellId);
      const bubble = sc && (sc.items || []).find((i) => i.type === "speechBubble" && i.id === sel?.bubbleId);
      if (sc && bubble) handleUpdateSpeechBubble(sc.id, bubble.id, updates);
    },
    onRemoveSpeechBubble: () => {
      const sel = s().selection;
      const sc = s().cells.find((c) => c.id === sel?.cellId);
      const bubble = sc && (sc.items || []).find((i) => i.type === "speechBubble" && i.id === sel?.bubbleId);
      if (sc && bubble) handleRemoveSpeechBubble(sc.id, bubble.id);
    },
    onDuplicateBubble: () => {
      const sel = s().selection;
      if (sel?.type === "bubble" && sel.cellId && sel.bubbleId) {
        handleDuplicateBubble(sel.cellId, sel.bubbleId);
      }
    },
    onDuplicateSFX: () => {
      const sel = s().selection;
      if (sel?.type === "sfx" && sel.cellId && sel.sfxId) {
        handleDuplicateSFX(sel.cellId, sel.sfxId);
      }
    },
    onUpdateCell: (updates) => {
      const sc = s().cells.find((c) => c.id === s().selection?.cellId);
      if (sc) handleUpdateCell(sc.id, updates);
    },
    onReorderItem: (direction) => {
      const sel = s().selection;
      const itemId = sel?.type === "bubble" ? sel.bubbleId : sel?.type === "sfx" ? sel.sfxId : null;
      if (itemId && sel.cellId) s().reorderItem(sel.cellId, itemId, direction);
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [store]);

  // ─── Render ─────────────────────────────────────────────────────────────
  const darkStyle = darkMode ? { background: "#191612", color: "#e8e2d5" } : {};

  // The work surface behind the page. Default is a neutral matte board;
  // the skeuomorphic wood grain sits behind DRAFTING_TABLE_PROPS.
  const woodSurface = darkMode
    ? [
        "radial-gradient(ellipse 1000px 520px at 50% -10%, rgba(255, 214, 140, 0.10), rgba(0, 0, 0, 0) 62%)",
        "repeating-linear-gradient(90deg, rgba(0, 0, 0, 0.20) 0px, rgba(0, 0, 0, 0.20) 2px, rgba(0, 0, 0, 0) 2px, rgba(0, 0, 0, 0) 118px)",
        "repeating-linear-gradient(90deg, rgba(255, 226, 170, 0.03) 0px, rgba(255, 226, 170, 0.03) 1px, rgba(0, 0, 0, 0) 1px, rgba(0, 0, 0, 0) 47px)",
        "linear-gradient(180deg, #2a2119, #1f1912)",
      ].join(", ")
    : [
        "radial-gradient(ellipse 1000px 520px at 50% -10%, rgba(255, 243, 205, 0.30), rgba(255, 243, 205, 0) 62%)",
        "repeating-linear-gradient(90deg, rgba(66, 40, 18, 0.08) 0px, rgba(66, 40, 18, 0.08) 2px, rgba(0, 0, 0, 0) 2px, rgba(0, 0, 0, 0) 118px)",
        "repeating-linear-gradient(90deg, rgba(255, 236, 190, 0.06) 0px, rgba(255, 236, 190, 0.06) 1px, rgba(0, 0, 0, 0) 1px, rgba(0, 0, 0, 0) 47px)",
        "linear-gradient(180deg, #a5815a, #92714e)",
      ].join(", ");

  const matteSurface = darkMode ? "#221f1b" : "#d9d6d0";


  // Masking tape holding the page down on the board
  const tapeBase = {
    position: "absolute",
    width: 90,
    height: 26,
    background: darkMode ? "rgba(214, 202, 168, 0.55)" : "rgba(235, 226, 195, 0.85)",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.18)",
    borderLeft: "1px dashed rgba(0, 0, 0, 0.10)",
    borderRight: "1px dashed rgba(0, 0, 0, 0.10)",
    pointerEvents: "none",
    zIndex: 70,
  };
  const tapeCorners = [
    { left: -34, top: -12, transform: "rotate(-45deg)" },
    { right: -34, top: -12, transform: "rotate(45deg)" },
    { left: -34, bottom: -12, transform: "rotate(45deg)" },
    { right: -34, bottom: -12, transform: "rotate(-45deg)" },
  ];

  // Sample page → a blank 3×3 (undoable)
  const clearSamplePage = () => {
    s().captureSnapshot();
    s().setLayoutTemplateKey(defaultCellLayoutTemplate.key);
    s().setCells(createInitialCells(defaultCellLayoutTemplate.grid));
    s().setIsSample(false);
    s().clearSelection();
    trackEvent("sample_page_cleared");
  };

  const sampleCard = (
    <div
      role="note"
      style={{
        position: "absolute",
        top: 14,
        left: 16,
        width: 230,
        zIndex: 80,
        background: "#fffdf6",
        border: "2px solid #1a1a1a",
        boxShadow: "4px 4px 0 rgba(0, 0, 0, 0.30)",
        transform: "rotate(-0.8deg)",
        padding: "10px 12px 12px",
        fontFamily: "'Georgia', serif",
        fontSize: 12,
        color: "#1a1a1a",
        lineHeight: 1.45,
      }}
    >
      <div
        style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          borderBottom: "2px solid rgba(0, 164, 228, 0.5)", paddingBottom: 6, marginBottom: 8,
        }}
      >
        <strong style={{ fontSize: 12, letterSpacing: 1.5, textTransform: "uppercase" }}>Sample page</strong>
        <button
          type="button"
          onClick={() => s().setIsSample(false)}
          aria-label="Keep the sample page and hide this note"
          style={{ border: "none", background: "transparent", color: "#1a1a1a", cursor: "pointer", fontSize: 15, lineHeight: 1, padding: "2px 4px" }}
        >
          ×
        </button>
      </div>
      <div style={{ marginBottom: 10 }}>
        Play with it — every bubble, cut, and gutter is real. Clear it when you're ready to draw.
      </div>
      <button
        type="button"
        onClick={clearSamplePage}
        style={{
          padding: "5px 10px", border: "1px solid #1a1a1a", background: "#ffe600", color: "#1a1a1a",
          fontFamily: "'Georgia', serif", fontSize: 12, cursor: "pointer", boxShadow: "2px 2px 0 rgba(0,0,0,0.25)",
        }}
      >
        Clear page
      </button>
    </div>
  );

  const dismissTips = () => {
    try {
      localStorage.setItem(TIPS_DISMISSED_KEY, "1");
    } catch (e) {
      // non-fatal: tips will reappear next session
    }
    setShowTips(false);
  };

  return (
    <EditorStoreContext.Provider value={store}>
      <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, ...darkStyle }}>
        <TopMenuBar
          onChangeLayoutTemplate={handleLayoutTemplateChange}
          onDownload={handleExport}
          onClearSavedData={handleClearSavedData}
          onExportJSON={handleExportJSON}
          onImportJSON={handleImportJSON}
          onCustomGrid={handleCustomGrid}
          onGutterSizeChange={handleGutterSizeChange}
          onPageSizeChange={handlePageSizeChange}
          onStoryboardChange={handleStoryboardChange}
        />

        <CanvasViewport
          pageWidth={portraitWidth}
          pageHeight={portraitHeight}
          background={DRAFTING_TABLE_PROPS ? woodSurface : matteSurface}
          darkMode={darkMode}
          onDeskClick={() => s().clearSelection()}
          overlay={<>
            <SelectionPalette
              onUploadBackground={handleUploadBackground}
              onClearTopItem={handleClearTopItem}
              onAddSpeechBubble={handleAddSpeechBubble}
              onUpdateItem={menuHandlers.onUpdateItem}
              onAddSFX={handleAddSFX}
              onUpdateSFX={menuHandlers.onUpdateSFX}
              onRemoveSFX={menuHandlers.onRemoveSFX}
              onUpdateSpeechBubble={menuHandlers.onUpdateSpeechBubble}
              onRemoveSpeechBubble={menuHandlers.onRemoveSpeechBubble}
              onDuplicateBubble={menuHandlers.onDuplicateBubble}
              onDuplicateSFX={menuHandlers.onDuplicateSFX}
              onUpdateCell={menuHandlers.onUpdateCell}
              onReorderItem={menuHandlers.onReorderItem}
              onDismissCell={handleDismissCell}
              onMergePanels={handleMergePanels}
              onUnmergePanels={handleUnmergePanels}
            />
            {isSample && sampleCard}{showTips && (
            <div
              style={{
                position: "absolute",
                top: 14,
                right: 16,
                width: 240,
                zIndex: 80,
                background: "#f6ecd0",
                border: "2px solid #1a1a1a",
                boxShadow: "4px 4px 0 rgba(0, 0, 0, 0.30)",
                transform: "rotate(0.6deg)",
                padding: "10px 12px 12px",
                fontFamily: "'Georgia', serif",
                fontSize: 12,
                color: "#1a1a1a",
                lineHeight: 1.45,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderBottom: "2px solid rgba(180, 35, 24, 0.45)",
                  paddingBottom: 6,
                  marginBottom: 8,
                }}
              >
                <strong style={{ fontSize: 12, letterSpacing: 1.5, textTransform: "uppercase" }}>
                  Layout Notes
                </strong>
                <button
                  type="button"
                  onClick={dismissTips}
                  aria-label="Dismiss layout tips"
                  style={{
                    border: "none", background: "transparent", color: "#1a1a1a",
                    cursor: "pointer", fontSize: 15, lineHeight: 1, padding: "2px 4px",
                  }}
                >
                  ×
                </button>
              </div>
              {storyboard ? (
                <>
                  <div style={{ marginBottom: 6 }}>
                    Frames stay locked to the board's aspect ratio — change Frame or Board in the toolbar to reshape them.
                  </div>
                  <div style={{ marginBottom: 6 }}>
                    Type shot notes right on the board; pick shot type and camera move in each header.
                  </div>
                  <div style={{ marginBottom: 6 }}>
                    Matte adds letterbox bars or a crop guide for another platform, like 9:16 for Reels.
                  </div>
                  <div style={{ marginBottom: 6 }}>
                    Drop or paste a sketch onto a frame. Add pages and shots keep numbering across them.
                  </div>
                </>
              ) : (
                <>
                  <div style={{ marginBottom: 6 }}>
                    Hold ⌥ and drag across a panel to cut it — at any angle. Shift cuts straight.
                  </div>
                  <div style={{ marginBottom: 6 }}>
                    Drag a gutter to resize both sides; double-click it to merge the panels.
                  </div>
                  <div style={{ marginBottom: 6 }}>
                    Double-click empty paper to letter there. Drop or paste art onto a panel.
                  </div>
                  <div style={{ marginBottom: 6 }}>
                    ⌘-scroll or pinch to zoom; hold Space and drag to move around the desk.
                  </div>
                </>
              )}
              <div>
                Work auto-saves as you go. Export PDF or PNG when it's ready for print.
              </div>
            </div>
          )}</>}
        >
          <div
            ref={containerRef}
            style={{
              position: "relative",
              width: portraitWidth,
              height: portraitHeight,
              minWidth: portraitWidth,
              minHeight: portraitHeight,
              border: comicStyle.page.frame,
              background: comicStyle.page.background,
              overflow: "visible",
              boxShadow: `${comicStyle.page.shadow}, 0 2px 6px rgba(0, 0, 0, 0.25)`,
            }}
            onClick={(event) => {
              if (event.target === event.currentTarget) s().clearSelection();
            }}
          >
            {DRAFTING_TABLE_PROPS && tapeCorners.map((corner, i) => (
              <div key={`tape-${i}`} aria-hidden="true" style={{ ...tapeBase, ...corner }} />
            ))}
          {/* Paper texture: Ben-Day halftone dots + edge vignette show through the
              gutters between panels, giving the page a printed-comic feel. */}
          {comicStyle.page.halftone && (
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 0,
                pointerEvents: "none",
                ...halftoneBackground(comicStyle.page.halftone),
              }}
            />
          )}
          {comicStyle.page.vignette && (
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 0,
                pointerEvents: "none",
                backgroundImage: comicStyle.page.vignette,
              }}
            />
          )}
          {cells.map((cell) => (
            <Cell
              key={cell.id}
              cell={cell}
              onStartDragEdge={startDragEdge}
              onUpdateSpeechBubble={handleUpdateSpeechBubble}
              onUpdateItem={handleUpdateItem}
              onUpdateSFX={handleUpdateSFX}
              onDuplicateBubble={handleDuplicateBubble}
              onDuplicateSFX={handleDuplicateSFX}
              onLetterAt={handleAddSpeechBubble}
              onDropImage={handleUploadBackground}
            />
          ))}

          {storyboard && cells.map((cell, index) => (
            <ShotNotes
              key={`notes-${cell.id}`}
              cell={cell}
              shotNumber={shotOffset + index + 1}
              compact={Boolean(boardLayout?.compact)}
              onChange={handleUpdateCell}
            />
          ))}

          {/* Knife and gutter handles reshape comic panels; board frames stay locked */}
          {!storyboard && (
            <LayoutTools pageRef={containerRef} pageWidth={portraitWidth} pageHeight={portraitHeight} />
          )}
          <InkSprites pageRef={containerRef} pageWidth={portraitWidth} pageHeight={portraitHeight} />

          {/* Print guide overlays */}
          {showGuides && (
            <>
              <div style={{
                position: "absolute", left: -BLEED_PX, top: -BLEED_PX,
                width: portraitWidth + BLEED_PX * 2, height: portraitHeight + BLEED_PX * 2,
                border: `1px dashed ${GUIDE_COLORS.bleed}`, pointerEvents: "none", zIndex: 60,
              }} />
              <div style={{
                position: "absolute", left: 0, top: 0,
                width: portraitWidth, height: portraitHeight,
                border: `1px solid ${GUIDE_COLORS.trim}`, pointerEvents: "none", zIndex: 60,
              }} />
              <div style={{
                position: "absolute", left: SAFE_ZONE_PX, top: SAFE_ZONE_PX,
                width: portraitWidth - SAFE_ZONE_PX * 2, height: portraitHeight - SAFE_ZONE_PX * 2,
                border: `1px dashed ${GUIDE_COLORS.safeZone}`, pointerEvents: "none", zIndex: 60,
              }} />
            </>
          )}
          </div>
        </CanvasViewport>

        {/* Context menu */}
        {contextMenu && (
          <div
            style={{ position: "fixed", inset: 0, zIndex: 200 }}
            onClick={() => s().setContextMenu(null)}
            onContextMenu={(e) => { e.preventDefault(); s().setContextMenu(null); }}
          >
            <div
              style={{
                position: "absolute", left: contextMenu.x, top: contextMenu.y,
                background: darkMode ? "#2a251e" : "#fffdf6",
                border: `2px solid ${darkMode ? "#4a4238" : "#1a1a1a"}`,
                borderRadius: 0,
                boxShadow: "4px 4px 0 rgba(0,0,0,0.25)", padding: "4px 0", minWidth: 160, zIndex: 201,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {[
                { action: "addBubble", label: "Add Speech Bubble", icon: "mode_comment" },
                { action: "addSFX", label: "Add SFX Text", icon: "text_fields" },
                ...(selection?.type === "bubble" ? [{ action: "copyBubble", label: "Copy Bubble", icon: "content_copy" }] : []),
                ...(clipboardBubble ? [{ action: "pasteBubble", label: "Paste Bubble", icon: "content_paste" }] : []),
                ...(selection?.type === "bubble" || selection?.type === "sfx" ? [
                  { action: "layerForward", label: "Bring Forward", icon: "arrow_upward" },
                  { action: "layerBackward", label: "Send Backward", icon: "arrow_downward" },
                  { action: "layerToFront", label: "Bring to Front", icon: "vertical_align_top" },
                  { action: "layerToBack", label: "Send to Back", icon: "vertical_align_bottom" },
                ] : []),
                { action: "delete", label: "Delete Panel", icon: "delete", destructive: true },
              ].map((item) => (
                <button
                  key={item.action}
                  type="button"
                  onClick={() => handleContextMenuAction(item.action)}
                  style={{
                    display: "flex", alignItems: "center", gap: 8, width: "100%",
                    padding: "6px 12px", border: "none", background: "transparent",
                    cursor: "pointer", fontSize: 12, fontFamily: "'Georgia', serif",
                    color: item.destructive
                      ? (darkMode ? "#f08c7d" : "#b42318")
                      : (darkMode ? "#e8e2d5" : "#1a1a1a"),
                    textAlign: "left",
                  }}
                  onMouseEnter={(e) => { e.target.style.background = darkMode ? "#3a332a" : "#f5f0e8"; }}
                  onMouseLeave={(e) => { e.target.style.background = "transparent"; }}
                >
                  <span className="material-icons" style={{ fontSize: 16 }}>{item.icon}</span>
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {showAIModal && selectedCell && (
          <AIGenerateModal
            onClose={() => s().setShowAIModal(false)}
            onGenerated={(imageUrl) => {
              const imageId = `ai-${Date.now()}`;
              s().applyCellUpdate((prevCells) =>
                prevCells.map((cell) => {
                  if (cell.id !== selectedCell.id) return cell;
                  const otherItems = (cell.items || []).filter((item) => item.type !== "background");
                  return {
                    ...cell,
                    items: [{
                      id: `background-${imageId}`,
                      type: "background",
                      url: imageUrl,
                      filename: "ai-generated.png",
                      createdAt: Date.now(),
                    }, ...otherItems],
                  };
                })
              );
            }}
          />
        )}
      </div>
    </EditorStoreContext.Provider>
  );
};

export default FluidLayoutEditor;
